import * as Immutable from "immutable";
import isDeepEqual from "stores/isDeepEqual";
import generateId from "logic/generateId";
export const createElasticsearchQueryString = (query = "") => ({
  type: "elasticsearch",
  query_string: query
});
const _streamFilters = (selectedStreams) => Immutable.List(selectedStreams == null ? void 0 : selectedStreams.map((stream) => Immutable.Map({ type: "stream", id: stream })));
const _streamCategoryFilters = (selectedCategories) => Immutable.List(selectedCategories == null ? void 0 : selectedCategories.map((category) => Immutable.Map({ type: "stream_category", category })));
export const filtersForQuery = (streams) => {
  if (!streams || streams.length === 0) {
    return null;
  }
  const streamFilters = _streamFilters(streams);
  return Immutable.Map({
    type: "or",
    filters: streamFilters
  });
};
export const categoryFiltersForQuery = (categories) => {
  if (!categories || categories.length === 0) {
    return null;
  }
  const categoryFilters = _streamCategoryFilters(categories);
  return Immutable.Map({
    type: "or",
    filters: categoryFilters
  });
};
export const newFiltersForQuery = (streams, categories) => {
  const streamFilter = _streamFilters(streams);
  const categoryFilter = _streamCategoryFilters(categories);
  if (streamFilter.size > 0 && categoryFilter.size > 0) {
    return Immutable.Map({
      type: "or",
      filters: Immutable.List(
        [...streamFilter.toArray(), ...categoryFilter.toArray()].map((filter) => filter)
      )
    });
  }
  return filtersForQuery(streams) || categoryFiltersForQuery(categories);
};
export const filtersToStreamSet = (filter) => {
  if (!filter) {
    return Immutable.Set();
  }
  const type = filter.get("type");
  if (type === "stream") {
    return Immutable.Set([filter.get("id")]);
  }
  const filters = filter.get("filters", Immutable.List());
  return filters.map(filtersToStreamSet).reduce((prev, cur) => prev.merge(cur), Immutable.Set());
};
export const filtersToStreamCategorySet = (filter) => {
  if (!filter) {
    return Immutable.Set();
  }
  const type = filter.get("type");
  if (type === "stream_category") {
    return Immutable.Set([filter.get("category")]);
  }
  const filters = filter.get("filters", Immutable.List());
  return filters.map(filtersToStreamCategorySet).reduce((prev, cur) => prev.merge(cur), Immutable.Set());
};
const isNullish = (o) => o === null || o === void 0;
export default class Query {
  constructor(id, query, timerange, filter, searchTypes, filters) {
    this._value = { id, query, timerange, filter, filters, searchTypes };
  }
  get id() {
    return this._value.id;
  }
  get query() {
    return this._value.query;
  }
  get timerange() {
    return this._value.timerange;
  }
  get filter() {
    return this._value.filter;
  }
  get filters() {
    return this._value.filters;
  }
  get searchTypes() {
    return this._value.searchTypes;
  }
  toBuilder() {
    const { id, query, timerange, filter, filters, searchTypes } = this._value;
    const builder = Query.builder().id(id).query(query).timerange(timerange).filter(filter).filters(filters).searchTypes(searchTypes);
    return filter ? builder.filter(filter) : builder;
  }
  equals(other) {
    if (other === void 0) {
      return false;
    }
    if (!(other instanceof Query)) {
      return false;
    }
    if (this.id !== other.id || !isDeepEqual(this.query, other.query) || !isDeepEqual(this.timerange, other.timerange) || !(isNullish(this.filter) && isNullish(other.filter) || isDeepEqual(this.filter, other.filter)) || !(isNullish(this.filters) && isNullish(other.filters) || isDeepEqual(this.filters, other.filters)) || !isDeepEqual(this.searchTypes, other.searchTypes)) {
      return false;
    }
    return true;
  }
  toJSON() {
    const { id, query, timerange, filter, filters, searchTypes } = this._value;
    return {
      id,
      query,
      timerange,
      filter,
      filters: filters == null ? void 0 : filters.toArray(),
      search_types: searchTypes
    };
  }
  static builder() {
    return new Builder().searchTypes([]);
  }
  static fromJSON(value) {
    const { id, query, timerange, filter, filters, search_types } = value;
    return new Query(
      id,
      query,
      timerange,
      Immutable.fromJS(filter),
      search_types,
      filters ? Immutable.List(filters) : void 0
    );
  }
}
class Builder {
  constructor(value = Immutable.Map()) {
    this.value = value;
  }
  id(value) {
    return new Builder(this.value.set("id", value));
  }
  newId() {
    return this.id(generateId());
  }
  query(value) {
    return new Builder(this.value.set("query", value));
  }
  timerange(value) {
    return new Builder(this.value.set("timerange", value));
  }
  filter(value) {
    return new Builder(this.value.set("filter", Immutable.fromJS(value)));
  }
  filters(value) {
    return new Builder(this.value.set("filters", value ? Immutable.List(value) : value));
  }
  searchTypes(value) {
    return new Builder(this.value.set("searchTypes", value));
  }
  build() {
    const { id, query, timerange, filter, filters, searchTypes } = this.value.toObject();
    return new Query(id, query, timerange, filter, searchTypes, filters);
  }
}
